package top.wcpe.allincore.ui.runtime.render.model

import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 不可变值对象，描述 styled 填充信息（圆角 + 边框 + 渐变 + 遮罩）。
 *
 * 用于 [RenderCommand] 的 ROUNDED_RECT / GRADIENT 命令。
 *
 * ADR-0072 决策 1 镜像：真源为 `platform-client-api` 的
 * `top.wcpe.allincore.platform.client.api.render.command.StyledFill`
 * （纯 Java 8 对外 API，按 ADR-0002 不得 KMP 化）。真源为 `@Getter` 值对象（无 equals），
 * 镜像同样不重写 equals；字段面、Builder 默认值与 has* 判定逻辑逐一照抄真源，
 * 一致性由 jvmTest 的 RenderModelConsistencyTest 逐字段 roundtrip 守护。
 */
// 字段面（16 字段）与 Builder 方法面（19 方法）照抄真源，由填充样式词汇表驱动而非业务堆积，
// 故豁免 LongParameterList / TooManyFunctions（同 static-analysis §4 集中登记口径的 fluent builder 类）；
// 径向默认值 0.5/1.0 与四角数组索引是照抄真源的设计数值，字面量即语义，豁免 MagicNumber（同 CornerRadii 先例）。
@Suppress("LongParameterList", "TooManyFunctions", "MagicNumber")
class StyledFill private constructor(
    @get:JvmName("cornerRadiusTL") val cornerRadiusTL: Float,
    @get:JvmName("cornerRadiusTR") val cornerRadiusTR: Float,
    @get:JvmName("cornerRadiusBR") val cornerRadiusBR: Float,
    @get:JvmName("cornerRadiusBL") val cornerRadiusBL: Float,
    @get:JvmName("borderWidth") val borderWidth: Float,
    @get:JvmName("borderColor") val borderColor: Int,
    @get:JvmName("gradientType") val gradientType: GradientType,
    @get:JvmName("gradientAngleRadians") val gradientAngleRadians: Float,
    gradientStops: List<GradientStop>?,
    /** 径向渐变中心，0..1 比例（0=左/上，1=右/下）；默认 0.5 即正中心。 */
    @get:JvmName("radialCenterX") val radialCenterX: Float,
    @get:JvmName("radialCenterY") val radialCenterY: Float,
    /** 径向渐变最大半径，相对短边的比例；默认 1.0（与无中心偏移的旧行为一致）。 */
    @get:JvmName("radialRadius") val radialRadius: Float,
    @get:JvmName("maskType") val maskType: MaskType,
    @get:JvmName("maskParam1") val maskParam1: Float,
    @get:JvmName("maskParam2") val maskParam2: Float,
    @get:JvmName("maskParam3") val maskParam3: Float,
) {

    /** 渐变停止点列表；null 入参归一为空列表（复刻真源），并做只读快照。 */
    @get:JvmName("gradientStops")
    val gradientStops: List<GradientStop> = gradientStops?.toList() ?: emptyList()

    /** 是否含有圆角信息。 */
    fun hasCornerRadius(): Boolean =
        cornerRadiusTL > 0 || cornerRadiusTR > 0 || cornerRadiusBR > 0 || cornerRadiusBL > 0

    /** 是否含有边框信息。 */
    fun hasBorder(): Boolean = borderWidth > 0

    /** 是否含有渐变信息。 */
    fun hasGradient(): Boolean = gradientType != GradientType.NONE && gradientStops.isNotEmpty()

    /** 是否含有遮罩信息。 */
    fun hasMask(): Boolean = maskType != MaskType.NONE

    /** styled 填充构建器：默认值（径向中心 0.5/0.5、半径 1.0、NONE 类型）与真源一致。 */
    class Builder internal constructor() {
        private var cornerRadiusTL = 0f
        private var cornerRadiusTR = 0f
        private var cornerRadiusBR = 0f
        private var cornerRadiusBL = 0f
        private var borderWidth = 0f
        private var borderColor = 0
        private var gradientType = GradientType.NONE
        private var gradientAngleRadians = 0f
        private var gradientStops: List<GradientStop>? = null

        // 径向渐变默认中心 (0.5, 0.5)、半径 1.0：与原行为完全一致，向后兼容。
        private var radialCenterX = 0.5f
        private var radialCenterY = 0.5f
        private var radialRadius = 1f
        private var maskType = MaskType.NONE
        private var maskParam1 = 0f
        private var maskParam2 = 0f
        private var maskParam3 = 0f

        /** 统一设置四角圆角半径。 */
        fun cornerRadius(r: Float): Builder = apply {
            cornerRadiusTL = r
            cornerRadiusTR = r
            cornerRadiusBR = r
            cornerRadiusBL = r
        }

        fun cornerRadiusTL(r: Float): Builder = apply { cornerRadiusTL = r }

        fun cornerRadiusTR(r: Float): Builder = apply { cornerRadiusTR = r }

        fun cornerRadiusBR(r: Float): Builder = apply { cornerRadiusBR = r }

        fun cornerRadiusBL(r: Float): Builder = apply { cornerRadiusBL = r }

        /**
         * 一次性设置四角圆角；`radii` 为 null 视作 `(0,0,0,0)`。
         * 调用顺序在统一 [cornerRadius] 之后会覆盖统一值。
         */
        fun cornerRadii(radii: FloatArray?): Builder = apply {
            if (radii == null || radii.size < CORNER_COUNT) {
                cornerRadiusTL = 0f
                cornerRadiusTR = 0f
                cornerRadiusBR = 0f
                cornerRadiusBL = 0f
            } else {
                cornerRadiusTL = radii[0]
                cornerRadiusTR = radii[1]
                cornerRadiusBR = radii[2]
                cornerRadiusBL = radii[3]
            }
        }

        fun borderWidth(w: Float): Builder = apply { borderWidth = w }

        fun borderColor(c: Int): Builder = apply { borderColor = c }

        fun gradientType(t: GradientType): Builder = apply { gradientType = t }

        fun gradientAngleRadians(a: Float): Builder = apply { gradientAngleRadians = a }

        fun gradientStops(stops: List<GradientStop>?): Builder = apply { gradientStops = stops }

        /** 径向渐变中心 X，0..1 比例（0=左边，1=右边）。 */
        fun radialCenterX(x: Float): Builder = apply { radialCenterX = x }

        /** 径向渐变中心 Y，0..1 比例（0=顶部，1=底部）。 */
        fun radialCenterY(y: Float): Builder = apply { radialCenterY = y }

        /** 径向渐变最大半径，相对短边的比例；默认 1.0。 */
        fun radialRadius(r: Float): Builder = apply { radialRadius = r }

        fun maskType(t: MaskType): Builder = apply { maskType = t }

        fun maskParam1(p: Float): Builder = apply { maskParam1 = p }

        fun maskParam2(p: Float): Builder = apply { maskParam2 = p }

        fun maskParam3(p: Float): Builder = apply { maskParam3 = p }

        fun build(): StyledFill = StyledFill(
            cornerRadiusTL, cornerRadiusTR, cornerRadiusBR, cornerRadiusBL,
            borderWidth, borderColor,
            gradientType, gradientAngleRadians, gradientStops,
            radialCenterX, radialCenterY, radialRadius,
            maskType, maskParam1, maskParam2, maskParam3,
        )

        private companion object {
            /** cornerRadii 数组的最小长度（四角）。 */
            const val CORNER_COUNT = 4
        }
    }

    companion object {
        /** 创建 StyledFill 的构建器。 */
        @JvmStatic
        fun builder(): Builder = Builder()
    }
}
